import type { ClassroomTeacherEnrollment } from '../enrollment';
import type { DigitalPracticeAccess } from './classroom-home-access';
import { isHomeAccess, isNoAccess } from './classroom-home-access';
import { isImportedFromGoogle } from './classroom-provider';
import {
  isCoreType,
  isJourneyType,
  type ClassroomType,
} from './classroom-type';

const DEFAULT_COURSE_ORDER_FOR_GROUPS = 6;

type Classroom = {
  id: string;
  name: string;
  courseId: string;
  courseName: string;
  courseOrder: number;
  icon: number;
  avatar: string | null;
  level?: string;
  providerId?: string | null;
  provider?: string | null;
  providerChecked?: boolean | null;
  checked?: boolean;
  numberOfStudents?: number;
  numberOfTeachers?: number;
  teachersNames?: string[];
  type?: ClassroomType;
  scheduleStart?: string;
  scheduleEnd?: string;
  digitalPracticeAccess?: DigitalPracticeAccess;
  digitalPracticeFirstDayOfWeek?: string;
  digitalPracticeFirstHourOfWeek?: string;
  pilotStatus?: { RTI?: boolean };
  alias?: string;
  source?: {
    name: string;
    id: string;
  };
  teacherEnrollments?: ClassroomTeacherEnrollment[];
};

const isCoreClassroom = (classroom?: Classroom): boolean => {
  return classroom?.type ? isCoreType(classroom.type) : false;
};

const isJourneyClassroom = (classroom?: Classroom): boolean => {
  return classroom?.type ? isJourneyType(classroom.type) : false;
};

const hasHomeAccess = (classroom?: Classroom): boolean => {
  return classroom?.digitalPracticeAccess
    ? isHomeAccess(classroom.digitalPracticeAccess)
    : false;
};

const hasNoAccess = (classroom?: Classroom): boolean => {
  return classroom?.digitalPracticeAccess
    ? isNoAccess(classroom.digitalPracticeAccess)
    : false;
};

const canAccessReports = (
  classroom: Classroom,
  isJourneysEnabled: boolean
): boolean => {
  if (!isJourneysEnabled) {
    return true;
  }

  return isJourneyClassroom(classroom as Classroom);
};

const isGoogleClassroom = (classroom?: Classroom): boolean => {
  return classroom?.provider ? isImportedFromGoogle(classroom.provider) : false;
};

const isClassroomCreatedManually = (classroom?: Classroom | null): boolean => {
  if (!classroom) {
    return true;
  }

  return !classroom?.source?.id;
};

export {
  canAccessReports,
  DEFAULT_COURSE_ORDER_FOR_GROUPS,
  hasHomeAccess,
  hasNoAccess,
  isClassroomCreatedManually,
  isCoreClassroom,
  isGoogleClassroom,
  isJourneyClassroom,
};
export type { Classroom };
